import React, { useEffect, useState, useRef } from "react";
import {
    Modal,
    Button,
    Form,
    Select,
    Table,
    message,
    Spin,
    Popconfirm,
    Input,
} from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import {
    fetch_all_labs,
    get_all_tests_labs,
    get_parameters,
    get_test_parameters,
    create_test_parameter,
    update_test_parameter,
    delete_test_parameter,
} from "../../endpoints/API";

const { Option } = Select;

const TestParameter = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [labs, setLabs] = useState([]);
    const [selectedLab, setSelectedLab] = useState(null);
    const [tests, setTests] = useState([]);
    const [parameters, setParameters] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const debounceRef = useRef(null);

    const fetchData = async (search = "", page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res = await get_test_parameters(search, page, pageSize);
            const responseData = res.data;
            setData(responseData.results || []);
            setPagination((prev) => ({
                ...prev,
                total: responseData.count || 0,
                current: page,
                pageSize: pageSize,
            }));
        } catch (error) {
            message.error("Failed to fetch test-parameter links");
        }
        setLoading(false);
    };

    const fetchLabs = async () => {
        try {
            const res = await fetch_all_labs();
            setLabs(res.data.labs || []);
        } catch (error) {
            message.error("Failed to fetch labs");
        }
    };

    const fetchParameters = async () => {
        try {
            const res = await get_parameters();
            setParameters(res.data.results || []);
        } catch (error) {
            message.error("Failed to fetch parameters");
        }
    };

    useEffect(() => {
        fetchData();
        fetchLabs();
        fetchParameters();
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (searchText.length === 0 || searchText.length >= 2) {
                fetchData(searchText, 1, pagination.pageSize);
            }
        }, 500);
        return () => clearTimeout(debounceRef.current);
    }, [searchText]);

    const handleTableChange = (pag) => {
        fetchData(searchText, pag.current, pag.pageSize);
    };

    const handleLabChange = async (labId) => {
        setSelectedLab(labId);
        form.setFieldsValue({ test_id: null });
        try {
            const res = await get_all_tests_labs(labId);
            setTests(res.data || []);
        } catch (error) {
            message.error("Failed to fetch tests for selected lab");
            setTests([]);
        }
    };

    const showModal = () => {
        setEditingRecord(null);
        setSelectedLab(null);
        setTests([]);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            test_id: record.test.id,
            parameter_id: record.parameter.id,
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await delete_test_parameter(id);
            message.success("Link deleted successfully");
            fetchData(searchText, pagination.current, pagination.pageSize);
        } catch (error) {
            message.error("Failed to delete link");
        }
    };

    const handleFormSubmit = async (values) => {
        try {
            if (editingRecord) {
                await update_test_parameter(editingRecord.id, values);
                message.success("Link updated successfully");
            } else {
                await create_test_parameter(values);
                message.success("Link created successfully");
            }
            setIsModalOpen(false);
            form.resetFields();
            fetchData(searchText, pagination.current, pagination.pageSize);
        } catch (error) {
            message.error(
                error?.response?.data?.detail || "Failed to save test-parameter link"
            );
        }
    };

    const columns = [
        {
            title: "Lab Name",
            dataIndex: ["test", "lab_name"],
            key: "lab_name",
        },
        {
            title: "Test Name",
            dataIndex: ["test", "name"],
            key: "test",
        },
        {
            title: "Parameter Name",
            dataIndex: ["parameter", "name"],
            key: "parameter",
        },
        {
            title: "Action",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button onClick={() => handleEdit(record)} icon={<EditOutlined />} />
                    <Popconfirm
                        title="Are you sure you want to delete this link?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div style={{ padding: "16px" }}>
            {/* Header row — wraps on mobile */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 16,
                    alignItems: "center",
                }}
            >
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search..."
                    style={{ width: "100%", maxWidth: 250 }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                {/* Uncomment if needed:
                <Button type="primary" onClick={showModal}>
                    Add Test-Parameter Link
                </Button> */}
            </div>

            {/* Scrollable table wrapper */}
            <Spin spinning={loading}>
                <div style={{ overflowX: "auto", width: "100%" }}>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey={(record) => record.id}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                        }}
                        onChange={(pag) => fetchData(searchText, pag.current, pag.pageSize)}
                        scroll={{ x: "max-content" }}
                    />
                </div>
            </Spin>

            <Modal
                title={editingRecord ? "Edit Test-Parameter Link" : "Add Test-Parameter Link"}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width="min(520px, 95vw)"
                style={{ top: 20 }}
                bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
            >
                <Form layout="vertical" form={form} onFinish={handleFormSubmit}>
                    {!editingRecord && (
                        <Form.Item
                            label="Select Lab"
                            name="lab_id"
                            rules={[{ required: true, message: "Please select a lab" }]}
                        >
                            <Select
                                placeholder="Select Lab"
                                onChange={handleLabChange}
                                showSearch
                                optionFilterProp="children"
                                style={{ width: "100%" }}
                            >
                                {labs.map((lab) => (
                                    <Option key={lab.id} value={lab.id}>
                                        {lab.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Select Test"
                        name="test_id"
                        rules={[{ required: true, message: "Please select a test" }]}
                    >
                        <Select
                            placeholder="Select Test"
                            showSearch
                            optionFilterProp="children"
                            disabled={!!editingRecord}
                            style={{ width: "100%" }}
                        >
                            {tests.map((test) => (
                                <Option key={test.id} value={test.id}>
                                    {test.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Select Parameter"
                        name="parameter_id"
                        rules={[{ required: true, message: "Please select a parameter" }]}
                    >
                        <Select
                            placeholder="Select Parameter"
                            showSearch
                            style={{ width: "100%" }}
                        >
                            {parameters.map((param) => (
                                <Option key={param.id} value={param.id}>
                                    {param.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                        <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit">Submit</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default TestParameter;